/**
 * Custom Input Component
 * Extends Ant Design Input with custom styling
 */

import React from 'react';
import { Input as AntInput, InputProps as AntInputProps } from 'antd';
import classNames from 'classnames';
import './CustomInput.scss';

export interface CustomInputProps extends Omit<AntInputProps, 'variant'> {
  /** Input variant */
  inputVariant?: 'default' | 'filled' | 'bordered';
  /** Show error state */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
}

/**
 * CustomInput Component
 */
export const CustomInput = React.forwardRef<any, CustomInputProps>(({
  inputVariant = 'default',
  error = false,
  errorMessage,
  className,
  ...rest
}, ref) => {
  const inputClasses = classNames(
    'custom-input',
    `custom-input--${inputVariant}`,
    {
      'custom-input--error': error,
    },
    className
  );

  return (
    <div className="custom-input-wrapper">
      <AntInput ref={ref} {...rest} className={inputClasses} />
      {error && errorMessage && (
        <span className="custom-input__error-message">{errorMessage}</span>
      )}
    </div>
  );
});

CustomInput.displayName = 'CustomInput';

/**
 * CustomTextArea Component
 */
export const CustomTextArea: React.FC<any> = ({ className, ...rest }) => {
  const textareaClasses = classNames('custom-textarea', className);
  return <AntInput.TextArea {...rest} className={textareaClasses} />;
};

/**
 * CustomPassword Component
 */
export const CustomPassword: React.FC<AntInputProps> = ({ className, ...rest }) => {
  const passwordClasses = classNames('custom-password', className);
  return <AntInput.Password {...rest} className={passwordClasses} />;
};

/**
 * CustomSearch Component
 */
export const CustomSearch: React.FC<AntInputProps> = ({ className, ...rest }) => {
  const searchClasses = classNames('custom-search', className);
  return <AntInput.Search {...rest} className={searchClasses} />;
};

export default CustomInput;

